<template>
  <div class="goods-statistics">
    <Spin v-if="loading" fix></Spin>

    <!-- 统计概览卡片 -->
    <Row :gutter="16" class="stats-overview">
      <Col span="6" v-for="item in overviewCards" :key="item.key">
        <Card class="stat-card">
          <div class="stat-icon" :style="{ backgroundColor: item.color }">
            <Icon :type="item.icon" size="24" color="#fff" />
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-trend" v-if="item.growth !== undefined">
              <span :class="item.growth >= 0 ? 'up' : 'down'">
                <Icon :type="item.growth >= 0 ? 'ios-arrow-up' : 'ios-arrow-down'" />
                {{ Math.abs(item.growth) }}%
              </span>
              <span class="trend-desc">较上期</span>
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="16">
        <Card title="商品销售趋势">
          <div ref="salesTrendChart" style="height: 350px;"></div>
        </Card>
      </Col>
      <Col span="8">
        <Card title="分类销售占比">
          <div ref="categoryChart" style="height: 350px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="12">
        <Card title="热销商品排行">
          <div ref="topGoodsChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="库存预警">
          <Alert v-if="lowStockItems.length > 0" type="warning" show-icon>
            有 {{ lowStockItems.length }} 个商品库存不足，请及时补货
          </Alert>
          <div v-if="lowStockItems.length > 0" style="max-height: 240px; overflow-y: auto; margin-top: 8px;">
            <div 
              v-for="item in lowStockItems" 
              :key="item.goodsId" 
              class="low-stock-item"
            >
              <div class="goods-info">
                <span class="goods-name">{{ item.goodsName }}</span>
                <Tag :color="item.stock === 0 ? 'error' : 'warning'" size="small">
                  库存: {{ item.stock }}
                </Tag>
              </div>
              <div class="goods-threshold">
                预警值: {{ item.threshold }}
              </div>
            </div>
          </div>
          <div v-else style="text-align: center; padding: 40px; color: #909399;">
            <Icon type="ios-checkmark-circle" size="48" color="#67C23A" />
            <p style="margin-top: 8px;">库存充足，无需补货</p>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 商品数据表格 -->
    <Card title="商品销售详细统计" style="margin-top: 16px;">
      <Table :columns="tableColumns" :data="tableData" border stripe />
    </Card>
  </div>
</template>

<script>
import { getGoodsStatistics } from '@/api/statistics'
import Echarts from 'echarts'

export default {
  name: 'GoodsStatistics',
  props: {
    dateRange: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      statsData: null,
      salesTrendChart: null,
      categoryChart: null,
      topGoodsChart: null,
      lowStockItems: [],
      tableColumns: [
        { title: '商品名称', key: 'goodsName', width: 180 },
        { title: '分类', key: 'categoryName', width: 100 },
        { title: '销量', key: 'salesCount', width: 100 },
        { title: '销售额', key: 'salesAmount', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${(p.row.salesAmount || 0).toFixed(2)}`) },
        { title: '库存', key: 'stock', width: 100, render: (h, p) => h('Tag', { props: { color: p.row.stock <= p.row.threshold ? 'error' : 'default' } }, p.row.stock) },
        { title: '转化率', key: 'conversionRate', width: 100, render: (h, p) => h('span', `${((p.row.conversionRate || 0) * 100).toFixed(1)}%`) }
      ],
      tableData: []
    }
  },
  computed: {
    overviewCards() {
      if (!this.statsData) return []
      const data = this.statsData
      return [
        {
          key: 'total',
          label: '商品总数',
          value: data.totalGoods || 0,
          icon: 'ios-cube',
          color: '#409EFF'
        },
        {
          key: 'sales',
          label: '总销量',
          value: this.formatNumber(data.totalSales || 0),
          growth: data.salesGrowth,
          icon: 'ios-trending-up',
          color: '#67C23A'
        },
        {
          key: 'revenue',
          label: '销售额',
          value: `¥${this.formatMoney(data.totalRevenue || 0)}`,
          growth: data.revenueGrowth,
          icon: 'logo-yen',
          color: '#F56C6C'
        },
        {
          key: 'lowStock',
          label: '库存预警',
          value: data.lowStockCount || 0,
          icon: 'ios-warning',
          color: data.lowStockCount > 0 ? '#E6A23C' : '#909399'
        }
      ]
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.loadData()
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const res = await getGoodsStatistics(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.statsData = res.data.data
          this.tableData = this.statsData.goodsDetails || []
          this.lowStockItems = this.statsData.lowStockItems || []
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || '获取商品统计数据失败')
        }
      } catch (error) {
        this.$Message.error('获取商品统计数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      this.initSalesTrendChart()
      this.initCategoryChart()
      this.initTopGoodsChart()
    },
    initSalesTrendChart() {
      if (!this.$refs.salesTrendChart || !this.statsData) return
      
      if (this.salesTrendChart) {
        this.salesTrendChart.dispose()
      }
      
      this.salesTrendChart = Echarts.init(this.$refs.salesTrendChart)
      const trendData = this.statsData.salesTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['销量', '销售额']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trendData.map(item => item.date)
        },
        yAxis: [
          {
            type: 'value',
            name: '销量',
            position: 'left'
          },
          {
            type: 'value',
            name: '销售额(元)',
            position: 'right'
          }
        ],
        series: [
          {
            name: '销量',
            type: 'bar',
            data: trendData.map(item => item.salesCount),
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '销售额',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: trendData.map(item => item.salesAmount),
            itemStyle: { color: '#F56C6C' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(245,108,108,0.3)' },
                  { offset: 1, color: 'rgba(245,108,108,0.05)' }
                ]
              }
            }
          }
        ]
      }
      
      this.salesTrendChart.setOption(option)
    },
    initCategoryChart() {
      if (!this.$refs.categoryChart || !this.statsData) return
      
      if (this.categoryChart) {
        this.categoryChart.dispose()
      }
      
      this.categoryChart = Echarts.init(this.$refs.categoryChart)
      const categoryData = this.statsData.categorySales || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: ¥{c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 20,
          data: categoryData.map(item => item.name)
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: categoryData
          }
        ],
        color: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#00CED1', '#BA55D3']
      }
      
      this.categoryChart.setOption(option)
    },
    initTopGoodsChart() {
      if (!this.$refs.topGoodsChart || !this.statsData) return
      
      if (this.topGoodsChart) {
        this.topGoodsChart.dispose()
      }
      
      this.topGoodsChart = Echarts.init(this.$refs.topGoodsChart)
      const topGoods = (this.statsData.topSalesGoods || []).slice(0, 10)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: topGoods.map(item => item.goodsName),
          inverse: true,
          axisLabel: {
            formatter: (value) => value.length > 8 ? value.substring(0, 8) + '...' : value
          }
        },
        series: [
          {
            type: 'bar',
            data: topGoods.map((item, index) => ({
              value: item.salesCount,
              itemStyle: {
                color: new Echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: index < 3 ? '#F56C6C' : '#409EFF' },
                  { offset: 1, color: index < 3 ? '#F9A7A7' : '#83bff6' }
                ])
              }
            }))
          }
        ]
      }
      
      this.topGoodsChart.setOption(option)
    },
    formatNumber(value) {
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + '万'
      }
      return value
    },
    formatMoney(value) {
      if (value >= 100000000) {
        return (value / 100000000).toFixed(2) + '亿'
      }
      if (value >= 10000) {
        return (value / 10000).toFixed(2) + '万'
      }
      return value.toFixed(2)
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', () => {
      this.salesTrendChart?.resize()
      this.categoryChart?.resize()
      this.topGoodsChart?.resize()
    })
  },
  beforeDestroy() {
    this.salesTrendChart?.dispose()
    this.categoryChart?.dispose()
    this.topGoodsChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.goods-statistics {
  .stats-overview {
    .stat-card {
      display: flex;
      align-items: center;
      padding: 20px;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 14px;
          color: #909399;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .up {
            color: #67C23A;
          }
          
          .down {
            color: #F56C6C;
          }
          
          .trend-desc {
            color: #909399;
            margin-left: 4px;
          }
        }
      }
    }
  }
  
  .low-stock-item {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    &:hover {
      background: #f9f9f9;
    }
    
    .goods-info {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .goods-name {
        font-weight: 500;
      }
    }
    
    .goods-threshold {
      color: #909399;
      font-size: 12px;
    }
  }
}
</style>

